/**
 * Route metadata for build-time HTML prerendering.
 * Each route gets a static HTML file with correct meta tags
 * so that social media crawlers and SEO tools see proper metadata.
 *
 * Slovenian URLs use the localized slugs from `src/routes/routeMap.ts`
 * so Googlebot sees `/sl/izdelki/varilne-glave` (not `/sl/products/welding-heads`).
 */

import { ROUTES, translatePath, type Lang } from "../src/routes/routeMap";
import { spotWeldingSlug } from "../src/lib/spotWeldingSlugMap";

export interface RouteMeta {
  /** Canonical EN-style path WITHOUT lang prefix (matches src/routes/routeMap.ts `en`). */
  path: string;
  /** SL-style path WITHOUT lang prefix (auto-derived from routeMap unless overridden). */
  slPath?: string;
  title: string;
  description: string;
  /** Slovenian title — falls back to EN if not provided */
  titleSl?: string;
  /** Slovenian meta description — falls back to EN if not provided */
  descriptionSl?: string;
  /** Optional absolute or site-relative OG/Twitter image override (default: DEFAULT_OG_IMAGE) */
  ogImage?: string;
  product?: {
    name: string;
    description: string;
    image?: string;
    category?: string;
  };
}

const SITE_NAME = "Kočevar d.o.o.";
const SITE_ORIGIN = "https://kocevar.eu";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "Manufacturer"],
  name: "Kočevar d.o.o.",
  legalName: "Kočevar, d.o.o.",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logos/expert-trafo.svg`,
  image: `${SITE_ORIGIN}/og-image.jpg`,
  description:
    "Slovenian manufacturer of industrial resistance welding machines and equipment — spot welders, projection welders, seam welders, micro welding systems and custom automated welding lines.",
  foundingDate: "1972",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sončna cesta 10",
    addressLocality: "Ločica ob Savinji",
    addressRegion: "Polzela",
    postalCode: "3313",
    addressCountry: "SI",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      telephone: "+386-3-703-22-50",
      email: "info@kocevar.eu",
      areaServed: ["SI", "EU"],
      availableLanguage: ["Slovenian", "English", "German"],
    },
    {
      "@type": "ContactPoint",
      contactType: "technical support",
      telephone: "+386-3-703-22-50",
      email: "info@kocevar.eu",
      areaServed: ["SI", "EU"],
      availableLanguage: ["Slovenian", "English", "German"],
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/kocevar",
    "https://www.youtube.com/@kocevar",
  ],
};

const ROUTE_METAS: RouteMeta[] = [
  { path: "/", title: "Expert Welding Solutions", description: "From cost-optimized welding machines to fully customized premium solutions — engineered to fit your process, not a catalog.",
    titleSl: "Strokovne rešitve za uporovno varjenje",
    descriptionSl: "Od cenovno optimiziranih varilnih naprav do popolnoma prilagojenih rešitev po meri — zasnovanih za vaš proizvodni proces, ne za katalog." },
  { path: "/about-us", title: "About Us", description: "Over 50 years of expertise in resistance welding technology. Kočevar d.o.o. — your trusted partner for industrial welding solutions.",
    titleSl: "O podjetju",
    descriptionSl: "Več kot 50 let strokovnega znanja na področju uporovnega varjenja. Kočevar d.o.o. — zaupanja vreden partner za industrijske varilne rešitve." },
  { path: "/products", title: "Products", description: "Industrial resistance welding machines — spot welders, projection welders, seam welders, micro welding heads, and workstations.",
    titleSl: "Izdelki",
    descriptionSl: "Industrijske naprave za uporovno varjenje — točkovni in bradavični varilni stroji, kolutni varilniki, mikro varilne glave in delovne postaje." },
  { path: "/solutions", title: "Solutions", description: "Custom-engineered welding solutions for gastronomy, automotive, battery, lock, and pillow plate applications.",
    titleSl: "Rešitve",
    descriptionSl: "Varilne rešitve po meri za gastronomijo, avtomobilsko industrijo, baterije, ključavnice in lamelne plošče." },
  { path: "/services", title: "Services", description: "Welding machine installation, maintenance, calibration, training, and technical support services.",
    titleSl: "Storitve",
    descriptionSl: "Montaža, vzdrževanje, kalibracija, izobraževanje in tehnična podpora za varilne naprave." },
  { path: "/knowledge-hub", title: "Knowledge Hub", description: "Technical articles, news, and insights about resistance welding technology and innovations.",
    titleSl: "Baza znanja",
    descriptionSl: "Strokovni članki, novice in vpogledi v tehnologijo uporovnega varjenja in najnovejše inovacije." },
  { path: "/faq", title: "FAQ", description: "Frequently asked questions about resistance welding machines, spare parts, and services.",
    titleSl: "Pogosta vprašanja",
    descriptionSl: "Pogosto zastavljena vprašanja o napravah za uporovno varjenje, rezervnih delih in storitvah." },
  { path: "/testimonials", title: "Testimonials", description: "Read what our clients say about Kočevar welding machines and service quality.",
    titleSl: "Mnenja strank",
    descriptionSl: "Preberite, kaj naše stranke pravijo o varilnih napravah Kočevar in kakovosti storitev." },
  { path: "/users-clients", title: "Users & Clients", description: "Trusted by leading manufacturers across automotive, gastronomy, electronics, and more.",
    titleSl: "Uporabniki in stranke",
    descriptionSl: "Zaupajo nam vodilni proizvajalci v avtomobilski industriji, gastronomiji, elektroniki in drugih panogah." },
  { path: "/partners", title: "Partners", description: "Our technology partners — Bosch Rexroth, Harms & Wende, and more.",
    titleSl: "Partnerji",
    descriptionSl: "Naši tehnološki partnerji — Bosch Rexroth, Harms & Wende in drugi." },
  { path: "/spare-parts", title: "Spare Parts & Components", description: "Original spare parts for resistance welding — seam welding heads, electrodes, transformers, and controllers.",
    titleSl: "Rezervni deli in komponente",
    descriptionSl: "Originalni rezervni deli za uporovno varjenje — varilne glave, elektrode, transformatorji in krmilniki." },
  { path: "/consumables", title: "Consumables & Electrodes", description: "Welding electrodes, electrode holders, and consumable parts for resistance welding machines.",
    titleSl: "Potrošni material in elektrode",
    descriptionSl: "Varilne elektrode, držala elektrod in potrošni deli za naprave za uporovno varjenje." },
  { path: "/splosni-pogoji", title: "Privacy Policy", description: "Privacy policy and terms of use for Kočevar d.o.o. website.",
    titleSl: "Politika zasebnosti",
    descriptionSl: "Politika zasebnosti in splošni pogoji uporabe spletne strani Kočevar d.o.o." },

  // Products
  { path: "/products/spot-welding", title: "Spot Welding Machines", description: "Standard spot welding machines — RS-81, RP-81, RB-81, RP-84, RT-94 and industrial welding guns.",
    titleSl: "Naprave za točkovno varjenje",
    descriptionSl: "Standardne naprave za točkovno varjenje — RS-81, RP-81, RB-81, RP-84, RT-94 in industrijske varilne klešče." },
  { path: "/products/industrial-pedestal-welders", title: "Industrial Pedestal Welders", description: "Industrial pedestal welders for gastronomy, medicine, railway, automotive, and electronics industries.",
    titleSl: "Industrijski stojalni varilniki",
    descriptionSl: "Industrijski stojalni varilniki za gastronomijo, medicino, železnico, avtomobilsko industrijo in elektroniko.",
    product: { name: "Industrial Pedestal Welders", description: "A great solution in the field of resistance welding technology for multiple industries.", image: "/products/pedestal-a135.webp", category: "Resistance Welding" } },
  { path: "/products/multi-spot-welding-machine", title: "Multi-spot Welding Machine", description: "Advanced multi-spot welders for railway, automotive, mesh, fencing, and electronics industries.",
    titleSl: "Več-točkovni varilni stroji",
    descriptionSl: "Napredni več-točkovni varilniki za železniško, avtomobilsko, mrežno, ograjno in elektronsko industrijo.",
    product: { name: "Multi-spot Welding Machine", description: "Advanced multi-spot welders for multiple industries.", image: "/products/multispot-a141.webp", category: "Resistance Welding" } },
  { path: "/products/industrial-projection-welders", title: "Invisible Welding Machine", description: "Invisible welding machine for multiple industries — AC and MFDC configurations.",
    titleSl: "Stroj za nevidno varjenje",
    descriptionSl: "Stroj za nevidno varjenje za različne panoge — izvedbe AC in MFDC.",
    product: { name: "Invisible Welding Machine", description: "Invisible welding machine — AC and MFDC configurations.", image: "/products/invisible-welding.webp", category: "Resistance Welding" } },
  { path: "/products/industrial-seam-welders", title: "Industrial Seam Welders", description: "High-performance seam welding machines for continuous, leak-proof welds across multiple industries.",
    titleSl: "Industrijski kolutni varilniki",
    descriptionSl: "Zmogljive naprave za kolutno varjenje za neprekinjene, neprepustne vare v različnih panogah.",
    product: { name: "Industrial Seam Welders", description: "High-performance seam welding machines for continuous, leak-proof welds.", image: "/products/seam-welder-hero.webp", category: "Resistance Welding" } },
  { path: "/products/welding-heads", title: "Welding Heads", description: "Precision micro-spot welding heads for lamp, automotive, electronics, and consumer goods industries.",
    titleSl: "Varilne glave",
    descriptionSl: "Precizne mikro varilne glave za industrijo svetil, avtomobilsko industrijo, elektroniko in izdelke široke porabe.",
    product: { name: "Welding Heads", description: "Precision micro-spot welding heads for multiple industries.", image: "/products/welding-head-sk2012.webp", category: "Micro Welding" } },
  { path: "/products/welding-pincers", title: "Welding Pincers", description: "Precision welding pincers for standard welding on difficult geometries, hot staking, and micro applications.",
    titleSl: "Varilne klešče",
    descriptionSl: "Precizne varilne klešče za standardno varjenje na zahtevnih geometrijah, vroče kovičenje in mikro aplikacije.",
    product: { name: "Welding Pincers", description: "Precision welding pincers for difficult geometries and micro applications.", image: "/products/pincers-sz2002.webp", category: "Micro Welding" } },
  { path: "/products/battery-processing", title: "Battery & Accumulator Processing", description: "Compact welding systems for battery and accumulator gap welding applications.",
    titleSl: "Obdelava baterij in akumulatorjev",
    descriptionSl: "Kompaktni varilni sistemi za varjenje baterij in akumulatorjev v reži (gap welding).",
    product: { name: "Battery & Accumulator Processing", description: "Compact welding systems for battery gap welding.", image: "/products/battery-kt-sst.webp", category: "Micro Welding" } },
  { path: "/products/workstation", title: "Welding Workstations", description: "Custom-designed manual and semi-automatic welding workstations for precision applications.",
    titleSl: "Varilne delovne postaje",
    descriptionSl: "Po meri zasnovane ročne in polavtomatske varilne delovne postaje za precizne aplikacije.",
    product: { name: "Welding Workstations", description: "Custom-designed manual and semi-automatic welding workstations.", image: "/products/workstation-manual.webp", category: "Workstation" } },
  { path: "/products/car-spotters", title: "Car Spotters", description: "Welding equipment for auto body repair — Car Point, Europei, and MFDC welding units.",
    titleSl: "Avtokleparski varilniki",
    descriptionSl: "Varilna oprema za popravila avtokarosej — Car Point, Europei in MFDC varilne enote." },
  { path: "/products/micro-welding", title: "Micro Welding", description: "Precision micro welding equipment — welding heads, pincers, compacting tools, and battery processing systems.",
    titleSl: "Mikro varjenje",
    descriptionSl: "Oprema za precizno mikro varjenje — varilne glave, klešče, stiskalna orodja in sistemi za obdelavo baterij." },
  { path: "/products/butt-welding", title: "Butt Welding", description: "RP-97 butt welding machine for joining wires, rods, and profiles end-to-end.",
    titleSl: "Sočelno varjenje",
    descriptionSl: "Stroj RP-97 za sočelno varjenje žic, palic in profilov." },
  { path: "/products/projection-welding", title: "Projection Welding", description: "RB-94 projection welding machine for nuts, bolts, and fasteners.",
    titleSl: "Bradavično varjenje",
    descriptionSl: "Stroj RB-94 za bradavično varjenje matic, vijakov in pritrdilnih elementov." },
  { path: "/products/ic-bending-tool", title: "IC Bending Tool", description: "Tools and machines for cutting and bending of Integrated Circuits (ICs), capacitors, and Hall sensors — for automotive electronics manufacturing.",
    titleSl: "Orodje za upogibanje IC",
    descriptionSl: "Orodja in stroji za rezanje in upogibanje integriranih vezij (IC), kondenzatorjev in Hallovih senzorjev — za proizvodnjo avtomobilske elektronike.",
    product: { name: "IC Bending Tool", description: "Precision tools and machines for cutting and bending of ICs, capacitors and Hall sensors.", image: "/products/ic-bending-tool.png", category: "Micro Welding" } },

  // Solutions
  { path: "/solutions/gastronomy-medicine", title: "Gastronomy & Medicine Solutions", description: "Welding solutions for gastronomy equipment and medical device manufacturing.",
    titleSl: "Rešitve za gastronomijo in medicino",
    descriptionSl: "Varilne rešitve za opremo v gastronomiji in proizvodnjo medicinskih pripomočkov." },
  {
    path: "/solutions/spot-welding-sinks",
    title: "Spot Welding Machine for Welding Sinks into Counters",
    description: "Specially designed spot welding machine for waterproof joining of stainless steel sinks into custom countertops — fast, watertight, no deformation. Patented Kočevar technology.",
    titleSl: "Stroj za točkovno varjenje pomivalnih korit v pulte",
    descriptionSl: "Posebej zasnovan stroj za točkovno varjenje za vodotesno spajanje nerjavnih korit v pulte po meri — hitro, vodotesno, brez deformacij. Patentirana tehnologija Kočevar.",
    ogImage: `${SITE_ORIGIN}/solutions/sink-welding-kocevar.png`,
    product: {
      name: "Kočevar Sink Welder — Spot Welding Machine for Sinks into Counters",
      description: "Specially designed spot welding machine for waterproof joining of stainless steel sink-basins into custom countertops. Watertight welds in ~120 seconds per sink, no sheet-metal deformation, automatic guidance — operated by a single worker.",
      image: "/solutions/sink-welding-kocevar.png",
      category: "Resistance Welding Machines / Special Machines",
    },
  },

  { path: "/solutions/robotic-spot-welding-guns", title: "Robotic Spot Welding Guns", description: "Custom robotic spot welding guns for automated production lines.",
    titleSl: "Robotske točkovne varilne klešče",
    descriptionSl: "Robotske točkovne varilne klešče po meri za avtomatizirane proizvodne linije." },
  { path: "/solutions/compacting-tools", title: "Compacting Tools", description: "Precision micro-welding tools for compacting cable strands, battery cells, and electronic components.",
    titleSl: "Stiskalna orodja",
    descriptionSl: "Precizna orodja za mikro varjenje pri stiskanju kabelskih žil, baterijskih celic in elektronskih komponent." },
  { path: "/solutions/lock-welding", title: "Lock Welding", description: "Industrial projection welder for precision welding of lock systems and security components.",
    titleSl: "Varjenje ključavnic",
    descriptionSl: "Industrijski bradavični varilnik za precizno varjenje sistemov ključavnic in varnostnih komponent." },

  // Spare parts
  { path: "/spare-parts/bosch-rexroth", title: "Bosch Rexroth Spare Parts", description: "Official Bosch Rexroth Certified Excellence Partner — welding controls, transformers, cabinets, and software.",
    titleSl: "Rezervni deli Bosch Rexroth",
    descriptionSl: "Uradni Bosch Rexroth Certified Excellence Partner — varilni krmilniki, transformatorji, omare in programska oprema." },
  { path: "/spare-parts/harms-wende", title: "Harms & Wende Spare Parts", description: "Official Harms & Wende partner — medium frequency inverters, controls, and friction welding systems.",
    titleSl: "Rezervni deli Harms & Wende",
    descriptionSl: "Uradni partner Harms & Wende — srednjefrekvenčni razsmerniki, krmilniki in sistemi za torno varjenje." },
  { path: "/products/welding-controls", title: "Welding Controls — MFDC Inverters", description: "Resistance welding controls — PXC7100, PRC7300, iSpot, Primus, Genius. MFDC technology from 0.5 kA to 130+ kVA.",
    titleSl: "Varilni krmilniki — MFDC razsmerniki",
    descriptionSl: "Krmilniki za uporovno varjenje — PXC7100, PRC7300, iSpot, Primus, Genius. Tehnologija MFDC od 0,5 kA do 130+ kVA." },
  { path: "/products/welding-controls/kt-sst-ac0c4-230", title: "KT-SST-AC0C4-230 — Compact AC Welding Control", description: "Compact AC welding control with integrated transformer. 4.0 kVA, 230 V / 16 A, air-cooled, portable design.",
    titleSl: "KT-SST-AC0C4-230 — Kompaktni AC varilni krmilnik",
    descriptionSl: "Kompaktni AC varilni krmilnik z integriranim transformatorjem. 4,0 kVA, 230 V / 16 A, zračno hlajen, prenosna izvedba.",
    product: { name: "KT-SST-AC0C4-230", description: "Compact AC welding control unit with integrated transformer for portable resistance welding.", image: "/products/welding-controls/kt-sst-ac0c4-230.png", category: "Welding Controls" } },
  { path: "/products/welding-controls/high-current-switch", title: "High Current Switch — Multi-Channel Welding Distribution", description: "Solid-state thyristor switching unit distributing welding current from one inverter to up to 5 welding heads or guns.",
    titleSl: "Visokotokovno stikalo — večkanalna distribucija varilnega toka",
    descriptionSl: "Tiristorska preklopna enota, ki razvaja varilni tok iz enega razsmernika do največ 5 varilnih glav ali klešč.",
    product: { name: "High Current Switch", description: "Thyristor-based AC switching cabinet for multi-channel resistance welding installations.", image: "/products/welding-controls/high-current-switch.png", category: "Welding Controls" } },
  { path: "/products/welding-controls/pxc7100", title: "PXC7100 — MFDC Welding Inverter (60 kVA)", description: "Mid-frequency 1–2 kHz MFDC welding control, 60 kVA power class, 8.9 V secondary — for spot, projection and seam welding.",
    titleSl: "PXC7100 — MFDC varilni razsmernik (60 kVA)",
    descriptionSl: "Srednjefrekvenčni 1–2 kHz MFDC varilni krmilnik, moč 60 kVA, sekundar 8,9 V — za točkovno, bradavično in kolutno varjenje.",
    product: { name: "PXC7100", description: "60 kVA MFDC welding inverter for medium-duty resistance welding.", image: "/products/welding-controls/pxc7100.png", category: "Welding Controls" } },
  { path: "/products/welding-controls/prc7300", title: "PRC7300 — High-Power MFDC Welding Inverter", description: "1 kHz MFDC welding inverter from 130 kVA — for body-in-white, multi-spot and high-current projection welding.",
    titleSl: "PRC7300 — Visokozmogljivi MFDC varilni razsmernik",
    descriptionSl: "1 kHz MFDC varilni razsmernik od 130 kVA naprej — za body-in-white, več-točkovno in visokotokovno bradavično varjenje.",
    product: { name: "PRC7300", description: "High-power 1 kHz MFDC welding inverter from 130 kVA.", image: "/products/welding-controls/prc7300.png", category: "Welding Controls" } },
  { path: "/products/welding-controls/ispot", title: "iSpot — High-Frequency Micro Welding Inverter", description: "10–15 kHz MFDC inverter for precision micro welding — 0.5 kA, 1 kA, 4 kA and 8 kA classes.",
    titleSl: "iSpot — Visokofrekvenčni razsmernik za mikro varjenje",
    descriptionSl: "10–15 kHz MFDC razsmernik za precizno mikro varjenje — razredi 0,5 kA, 1 kA, 4 kA in 8 kA.",
    product: { name: "iSpot", description: "High-frequency MFDC micro welding inverter, 0.5–8 kA classes.", image: "/products/welding-controls/ispot.jpg", category: "Welding Controls" } },
  { path: "/products/welding-controls/primus", title: "Primus — 10 kHz MFDC Welding Inverter", description: "10 kHz MFDC inverter in 5 kA and 10 kA classes for precision and medium-current resistance welding.",
    titleSl: "Primus — 10 kHz MFDC varilni razsmernik",
    descriptionSl: "10 kHz MFDC razsmernik v razredih 5 kA in 10 kA za precizno in srednjetokovno uporovno varjenje.",
    product: { name: "Primus", description: "10 kHz MFDC welding inverter, 5 kA and 10 kA classes.", image: "/products/welding-controls/primus.jpg", category: "Welding Controls" } },
  { path: "/products/welding-controls/genius", title: "Genius — Modular MFDC Welding Inverter", description: "Modular 1 kHz MFDC welding inverter from 42 kVA — for projection presses, multi-spot lines and custom industrial cells.",
    titleSl: "Genius — Modularni MFDC varilni razsmernik",
    descriptionSl: "Modularni 1 kHz MFDC varilni razsmernik od 42 kVA — za bradavične stiskalnice, več-točkovne linije in industrijske celice po meri.",
    product: { name: "Genius", description: "Modular 1 kHz MFDC welding inverter from 42 kVA upwards.", image: "/products/welding-controls/genius.webp", category: "Welding Controls" } },

  // Accessories
  { path: "/accessories", title: "Accessories", description: "Welding accessories — tip dressers, electrode holders, and auxiliary tooling for resistance welding machines.",
    titleSl: "Dodatna oprema",
    descriptionSl: "Dodatna varilna oprema — naprave za obnavljanje elektrod, držala elektrod in pomožno orodje za naprave za uporovno varjenje." },
  { path: "/accessories/tip-dressers", title: "Tip Dressers", description: "Electrode tip dressers for maintaining welding electrode quality and extending electrode life.",
    titleSl: "Naprave za obnavljanje konic elektrod",
    descriptionSl: "Naprave za obnavljanje konic elektrod za vzdrževanje kakovosti varilnih elektrod in podaljšanje njihove življenjske dobe." },

  // Additional products
  { path: "/products/spot-welding/rs-81", title: "RS-81 / RP-81 Pedestal Spot Welder", description: "Pedestal AC spot/projection welder for industrial sheet-metal welding.",
    titleSl: "RS-81 / RP-81 stojalni točkovni varilnik",
    descriptionSl: "Stojalni AC točkovni/bradavični varilnik za industrijsko varjenje pločevine." },
  { path: "/products/spot-welding/rb-81", title: "RB-81 Pedestal Projection Welder", description: "Pedestal AC projection welder for nut and bolt welding on sheet-metal parts.",
    titleSl: "RB-81 stojalni bradavični varilnik",
    descriptionSl: "Stojalni AC bradavični varilnik za varjenje matic in vijakov na pločevino." },
  { path: "/products/spot-welding/rt-94", title: "RT-94 Pedestal Spot Welder", description: "Heavy-duty pedestal spot welder for thicker sheet-metal and industrial applications.",
    titleSl: "RT-94 stojalni točkovni varilnik",
    descriptionSl: "Zmogljiv stojalni točkovni varilnik za debelejšo pločevino in industrijske aplikacije." },
  { path: "/products/spot-welding/rp-84", title: "RP-84 Pedestal Spot Welder", description: "Pedestal spot welder for general industrial sheet-metal welding.",
    titleSl: "RP-84 stojalni točkovni varilnik",
    descriptionSl: "Stojalni točkovni varilnik za splošno industrijsko varjenje pločevine." },
  { path: "/products/spot-welding/rp-96-x", title: "RP-96-X Pedestal Spot Welder", description: "MFDC pedestal spot welder with X-style electrode arms for difficult-access geometries.",
    titleSl: "RP-96-X stojalni točkovni varilnik",
    descriptionSl: "MFDC stojalni točkovni varilnik z elektrodnimi rokami v obliki X za težko dostopne geometrije." },
  { path: "/products/spot-welding/rp-96-c", title: "RP-96-C Pedestal Spot Welder", description: "MFDC pedestal spot welder with C-style electrode arms for standard sheet-metal welding.",
    titleSl: "RP-96-C stojalni točkovni varilnik",
    descriptionSl: "MFDC stojalni točkovni varilnik z elektrodnimi rokami v obliki C za standardno varjenje pločevine." },
  { path: "/products/spot-welding/fast-spotter", title: "RP-84/3,5-ECO Fast Spotter", description: "Compact spot welder for stud welding and fast spotting applications.",
    titleSl: "Aparat za varjenje navojnih čepov",
    descriptionSl: "Kompakten točkovni varilnik za varjenje navojnih čepov in hitre točkovne aplikacije." },
  { path: "/products/spot-welding/rgs", title: "RGS Modular Twin Spot Welder", description: "Modular twin spot welder for parallel-spot welding lines and assemblies.",
    titleSl: "RGS modularni dvojni točkovni varilnik",
    descriptionSl: "Modularni dvojni točkovni varilnik za vzporedne točkovne linije in sklope." },
  { path: "/products/spot-welding/rdr", title: "RDR Suspended Twin Spot Welder", description: "Suspended twin spot welder for ergonomic, double-electrode manual welding.",
    titleSl: "RDR viseči dvojni točkovni varilnik",
    descriptionSl: "Viseči dvojni točkovni varilnik za ergonomsko ročno varjenje z dvojnimi elektrodami." },
  { path: "/products/spot-welding/hand-held-welding-gun", title: "R-93-RA Hand-held Welding Gun", description: "Portable hand-held welding gun for on-site and difficult-access spot welding.",
    titleSl: "Ročni varilni točkalnik R-93-RA",
    descriptionSl: "Prenosni ročni varilni točkalnik za točkovno varjenje na terenu in v težko dostopnih mestih." },
  { path: "/products/seam-welding/rk-94", title: "RK-94 Seam Welder", description: "High-performance seam welding machine for continuous, leak-proof welds.",
    titleSl: "RK-94 kolutni varilnik",
    descriptionSl: "Zmogljiv kolutni varilnik za neprekinjene, neprepustne vare." },
  { path: "/products/servo-welding-heads", title: "Servo Welding Heads", description: "Servo-driven precision welding heads for repeatable micro-welding applications.",
    titleSl: "Servo varilne glave",
    descriptionSl: "Servomotorsko gnane precizne varilne glave za ponovljive aplikacije mikro varjenja." },
  { path: "/products/double-welding-heads", title: "Double Welding Heads", description: "Twin-electrode welding heads for parallel-gap and series spot welding applications.",
    titleSl: "Dvojne varilne glave",
    descriptionSl: "Varilne glave z dvojnimi elektrodami za parallel-gap in zaporedne točkovne aplikacije." },

  // Additional spare parts
  { path: "/spare-parts/transformers", title: "Welding Transformers", description: "Replacement welding transformers for resistance welding machines.",
    titleSl: "Varilni transformatorji",
    descriptionSl: "Nadomestni varilni transformatorji za naprave za uporovno varjenje." },
  { path: "/spare-parts/welding-controls", title: "Welding Controls Spare Parts", description: "Replacement welding controllers and electronic modules for resistance welding systems.",
    titleSl: "Rezervni deli za varilne krmilnike",
    descriptionSl: "Nadomestni varilni krmilniki in elektronski moduli za sisteme uporovnega varjenja." },

  // Additional solutions
  { path: "/solutions/tension-ring-welding", title: "Tension Ring Welding", description: "Custom welding solutions for tension rings on industrial barrels and containers.",
    titleSl: "Varjenje napenjalnih obročev",
    descriptionSl: "Varilne rešitve po meri za napenjalne obroče na industrijskih sodih in posodah." },
  { path: "/solutions/barrel-handle-welding", title: "Barrel Handle Welding", description: "Automated welding of handles onto industrial barrels and drums.",
    titleSl: "Varjenje ročajev sodov",
    descriptionSl: "Avtomatizirano varjenje ročajev na industrijske sode in bobne." },
  { path: "/solutions/tapping-barrel-welding", title: "Tapping Barrel Welding", description: "Specialized welding solutions for beer-keg and tapping-barrel production.",
    titleSl: "Varjenje točilnih sodov",
    descriptionSl: "Specializirane varilne rešitve za proizvodnjo pivskih in točilnih sodov." },
  { path: "/solutions/protective-mesh-welding", title: "Protective Mesh Welding", description: "Multi-spot welding machines for protective mesh and grating production.",
    titleSl: "Varjenje zaščitnih mrež",
    descriptionSl: "Več-točkovne varilne naprave za proizvodnjo zaščitnih mrež in rešetk." },
  { path: "/solutions/oil-tank-welding", title: "Oil Tank Welding", description: "Seam welding solutions for fuel and oil tank manufacturing.",
    titleSl: "Varjenje rezervoarjev za olje",
    descriptionSl: "Kolutne varilne rešitve za proizvodnjo rezervoarjev za gorivo in olje." },
  { path: "/solutions/double-sided-seam-oven-welding", title: "Double-sided Seam Welding for Ovens", description: "Double-sided seam welding lines for oven and appliance manufacturing.",
    titleSl: "Dvostransko kolutno varjenje za pečice",
    descriptionSl: "Linije za dvostransko kolutno varjenje v proizvodnji pečic in gospodinjskih aparatov." },
  { path: "/solutions/chimney-elbow-welding", title: "Chimney Elbow Welding", description: "Automated seam welding solutions for chimney elbow and ducting production.",
    titleSl: "Varjenje dimniških kolen",
    descriptionSl: "Avtomatizirane kolutne varilne rešitve za proizvodnjo dimniških kolen in cevovodov." },
  { path: "/solutions/weld-destructive-test", title: "Weld Destructive Test", description: "Destructive testing equipment and services for weld quality verification.",
    titleSl: "Porušitveni preizkus zvarov",
    descriptionSl: "Oprema in storitve porušitvenega preizkušanja za preverjanje kakovosti zvarov." },
  { path: "/solutions/aluminum-frame-welding", title: "Aluminum Frame Welding", description: "Specialized welding lines for aluminum frame and structural assemblies.",
    titleSl: "Varjenje aluminijastih okvirjev",
    descriptionSl: "Specializirane varilne linije za aluminijaste okvirje in strukturne sklope." },
  { path: "/solutions/door-profile-seam-welding", title: "Door Profile Seam Welding", description: "Resistance seam welding machine for continuous, repeatable welding of door profiles.",
    titleSl: "Kolutno varjenje vratnega profila",
    descriptionSl: "Stroj za uporovno kolutno varjenje, namenjen neprekinjenemu in ponovljivemu varjenju vratnih profilov." },
  { path: "/solutions/nut-welding", title: "Nut Welding Machine", description: "Projection welding machine with automatic nut feeding for repeatable welding of nuts onto sheet metal parts.",
    titleSl: "Stroj za varjenje matic",
    descriptionSl: "Stroj za bradavičasto varjenje z avtomatskim dodajanjem matic za ponovljivo varjenje matic na pločevinaste dele." },
];


// Auto-derive SL slug from routeMap (translatePath returns EN path unchanged when no mapping exists).
export const routes: RouteMeta[] = ROUTE_METAS.map((r) => ({
  ...r,
  slPath: r.slPath ?? translatePath(r.path, "sl"),
}));

/**
 * Lookup helper for runtime PageMeta: finds the bilingual entry for a given
 * EN-style path (no lang prefix). Returns undefined when no entry exists
 * (dynamic routes like news articles), so the caller can fall back to props.
 */
export function getRouteMeta(enPath: string): RouteMeta | undefined {
  return routes.find((r) => r.path === enPath);
}

const BREADCRUMB_LABELS: Record<string, string> = {
  products: "Products",
  solutions: "Solutions",
  services: "Services",
  "spare-parts": "Spare Parts & Components",
  consumables: "Consumables & Electrodes",
  accessories: "Accessories",
  "knowledge-hub": "Knowledge Hub",
  faq: "FAQ",
  testimonials: "Testimonials",
  "users-clients": "Users & Clients",
  partners: "Partners",
  "about-us": "About Us",
  "splosni-pogoji": "Privacy Policy",
};

function buildBreadcrumbJsonLd(routePath: string, pageTitle: string): object | null {
  const segments = routePath.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const items: { "@type": string; position: number; name: string; item?: string }[] = [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
  ];

  let path = "";
  for (let i = 0; i < segments.length; i++) {
    path += `/${segments[i]}`;
    const isLast = i === segments.length - 1;
    const label = isLast
      ? pageTitle
      : BREADCRUMB_LABELS[segments[i]] || segments[i].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    const entry: { "@type": string; position: number; name: string; item?: string } = {
      "@type": "ListItem",
      position: i + 2,
      name: label,
    };
    if (!isLast) entry.item = `${SITE_ORIGIN}${path}`;
    items.push(entry);
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

/** Build the absolute, lang-prefixed URL for a route in the given language. */
export function routeUrl(route: RouteMeta, lang: Lang): string {
  let slug = lang === "sl" ? (route.slPath ?? route.path) : route.path;
  // Apply SL slug mapping for spot-welding machine detail pages
  // (routeMap fills :machineId verbatim, so we translate it here).
  const spotMatch = slug.match(/^(\/(?:products\/spot-welding|izdelki\/tockovno-varjenje))\/([^/?#]+)(.*)$/);
  if (spotMatch) {
    const [, prefix, id, rest] = spotMatch;
    slug = `${prefix}/${spotWeldingSlug(id, lang)}${rest}`;
  }
  const suffix = slug === "/" ? "" : slug;
  return `${SITE_ORIGIN}/${lang}${suffix}`;
}

/**
 * Generate the <head> content to inject for a given route in a given language.
 */
export function generateHeadTags(route: RouteMeta, lang: Lang = "en"): string {
  const localizedTitle = lang === "sl" && route.titleSl ? route.titleSl : route.title;
  const localizedDescription = lang === "sl" && route.descriptionSl ? route.descriptionSl : route.description;
  const fullTitle = `${localizedTitle} | ${SITE_NAME}`;
  const enUrl = routeUrl(route, "en");
  const slUrl = routeUrl(route, "sl");
  const canonicalUrl = lang === "sl" ? slUrl : enUrl;
  const ogImage = route.ogImage
    ? (route.ogImage.startsWith("http") ? route.ogImage : `${SITE_ORIGIN}${route.ogImage}`)
    : DEFAULT_OG_IMAGE;
  const ogLocale = lang === "sl" ? "sl_SI" : "en_US";
  const ogLocaleAlt = lang === "sl" ? "en_US" : "sl_SI";

  let head = `
    <title>${escHtml(fullTitle)}</title>
    <meta name="description" content="${escHtml(localizedDescription)}">
    <link rel="canonical" href="${canonicalUrl}">
    <link rel="alternate" hreflang="en" href="${enUrl}">
    <link rel="alternate" hreflang="sl" href="${slUrl}">
    <link rel="alternate" hreflang="x-default" href="${enUrl}">
    <meta property="og:title" content="${escHtml(fullTitle)}">
    <meta property="og:description" content="${escHtml(localizedDescription)}">
    <meta property="og:image" content="${ogImage}">
    <meta property="og:image:alt" content="${escHtml(route.product?.name || localizedTitle)}">
    <meta property="og:url" content="${canonicalUrl}">
    <meta property="og:type" content="${route.product ? "product" : "website"}">
    <meta property="og:site_name" content="${escHtml(SITE_NAME)}">
    <meta property="og:locale" content="${ogLocale}">
    <meta property="og:locale:alternate" content="${ogLocaleAlt}">
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:title" content="${escHtml(fullTitle)}">
    <meta name="twitter:description" content="${escHtml(localizedDescription)}">
    <meta name="twitter:image" content="${ogImage}">
    <meta name="twitter:image:alt" content="${escHtml(route.product?.name || localizedTitle)}">
    <script type="application/ld+json">${JSON.stringify(ORGANIZATION_JSONLD)}</script>`;


  if (route.product) {
    const productJsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: route.product.name,
      description: route.product.description,
      image: route.product.image ? `${SITE_ORIGIN}${route.product.image}` : undefined,
      category: route.product.category,
      url: canonicalUrl,
      brand: { "@type": "Brand", name: "Kočevar" },
      manufacturer: { "@type": "Organization", name: "Kočevar d.o.o.", url: SITE_ORIGIN },
    };
    head += `\n    <script type="application/ld+json">${JSON.stringify(productJsonLd)}</script>`;
  }

  const breadcrumb = buildBreadcrumbJsonLd(route.path, localizedTitle);
  if (breadcrumb) {
    head += `\n    <script type="application/ld+json">${JSON.stringify(breadcrumb)}</script>`;
  }

  return head;
}

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
